import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  Eye,
  MousePointerClick,
  ShoppingCart,
  TrendingUp,
  Play,
  Image as ImageIcon,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = 'https://api.glowytics.com';
const FALLBACK_IMG = '/Post1212112.png';

// Kampanya adinda "Go" gecenleri yakala (kelime sinirli; "google", "gofret" gibi kelimeleri ELEME).
const GO_REGEX = /(^|[^a-z0-9])go([^a-z0-9]|$)/i;
const isGoCampaign = (name) => !!name && GO_REGEX.test(String(name));

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtTRY = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(num(v));
const fmtTRY2 = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(num(v));
const fmtNum = (v) => new Intl.NumberFormat('tr-TR').format(Math.round(num(v)));
const fmtPct = (v) => `%${num(v).toFixed(2)}`;

const DATE_RANGES = [
  { value: 'all', label: 'Tum zamanlar' },
  { value: 'last7', label: 'Son 7 gun' },
  { value: 'last30', label: 'Son 30 gun' },
  { value: 'last90', label: 'Son 90 gun' },
  { value: 'thisMonth', label: 'Bu ay' },
  { value: 'lastMonth', label: 'Gecen ay' },
  { value: 'custom', label: 'Ozel aralik' },
];

export default function MetaGoSpendReport() {
  const { selectedBrand } = useAuth();
  const [dateRange, setDateRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [mediaMap, setMediaMap] = useState({});
  const [previewMap, setPreviewMap] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  // Canli Meta pivot (campaign-insights, anlik Graph API istegi)
  const [pivot, setPivot] = useState({ campaigns: [], totals: null });
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState('');

  const brandId =
    selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '');

  const fetchData = useCallback(async () => {
    if (!brandId) {
      setError('Aktif marka bulunamadi.');
      return;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) {
      return; // ozel aralik secili ama tarihler henuz girilmemis
    }
    setLoading(true);
    setError('');
    setPreviewMap({});
    try {
      const params = new URLSearchParams({ dateRange });
      if (dateRange === 'custom') {
        params.set('startDate', customStart);
        params.set('endDate', customEnd);
      }
      const headers = { 'X-Brand-ID': brandId };

      const perfResp = await axios.get(
        `${API}/api/google-ads/meta-ad-performance-with-creatives?${params.toString()}`,
        { withCredentials: true, headers }
      );
      const allRows = Array.isArray(perfResp?.data?.rows) ? perfResp.data.rows : [];
      const goRows = allRows.filter((r) => isGoCampaign(r.campaign_name));
      setRows(goRows);

      // Creative detaylarini (preview gorselleri) tek seferde cek
      const creativeIds = [...new Set(goRows.map((r) => r.creative_id).filter(Boolean))];
      if (creativeIds.length > 0) {
        const map = {};
        const CHUNK = 100;
        for (let i = 0; i < creativeIds.length; i += CHUNK) {
          const slice = creativeIds.slice(i, i + CHUNK);
          const detResp = await axios.get(`${API}/api/google-ads/meta-creative-details`, {
            params: { creativeIds: slice.join(',') },
            withCredentials: true,
            headers,
          });
          const detRows = Array.isArray(detResp?.data?.rows) ? detResp.data.rows : [];
          detRows.forEach((d) => {
            if (d.creative_id) map[d.creative_id] = d;
          });
        }
        setMediaMap(map);
      } else {
        setMediaMap({});
      }
      setLastUpdated(new Date());

      // Meta canli preview HTML'lerini arka planda getir (creative-performance ile ayni endpoint).
      // UI'yi bloklamadan; geldikce previewMap'e dolar, gelmezse thumbnail fallback kalir.
      if (creativeIds.length > 0) {
        (async () => {
          const CHUNK_P = 20;
          for (let i = 0; i < creativeIds.length; i += CHUNK_P) {
            const slice = creativeIds.slice(i, i + CHUNK_P);
            try {
              const pResp = await axios.get(`${API}/api/meta-ads/live/creative-preview`, {
                params: { creativeIds: slice.join(','), adFormat: 'MOBILE_FEED_STANDARD' },
                withCredentials: true,
                headers,
              });
              const pRows = Array.isArray(pResp?.data?.rows) ? pResp.data.rows : [];
              if (pRows.length) {
                setPreviewMap((prev) => {
                  const next = { ...prev };
                  pRows.forEach((r) => {
                    if (r?.creative_id && r?.body) next[r.creative_id] = r.body;
                  });
                  return next;
                });
              }
            } catch (e) {
              /* preview opsiyonel - sessizce gec, thumbnail fallback devrede */
            }
          }
        })();
      }
    } catch (err) {
      console.error('[MetaGoSpendReport] fetch error', err);
      setError(err?.response?.data?.error || err.message || 'Veri alinamadi.');
      setRows([]);
      setMediaMap({});
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange, customStart, customEnd]);

  // Canli Meta kampanya pivotu (anlik Graph API istegi)
  const fetchPivot = useCallback(async () => {
    if (!brandId) return;
    if (dateRange === 'custom' && (!customStart || !customEnd)) return;
    setPivotLoading(true);
    setPivotError('');
    try {
      const params = new URLSearchParams({ dateRange, nameContains: 'go' });
      if (dateRange === 'custom') {
        params.set('startDate', customStart);
        params.set('endDate', customEnd);
      }
      const resp = await axios.get(`${API}/api/meta-ads/live/campaign-insights?${params.toString()}`, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      const all = Array.isArray(resp?.data?.campaigns) ? resp.data.campaigns : [];
      // Server "go" substring ile daratı; burada kelime-sınırlı regex ile netleştir.
      const goOnly = all.filter((c) => isGoCampaign(c.campaign_name));
      const totals = goOnly.reduce(
        (t, c) => ({
          spend: t.spend + num(c.spend),
          impressions: t.impressions + num(c.impressions),
          clicks: t.clicks + num(c.clicks),
          conversions: t.conversions + num(c.conversions),
          revenue: t.revenue + num(c.revenue),
        }),
        { spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 }
      );
      setPivot({ campaigns: goOnly, totals });
    } catch (err) {
      console.error('[MetaGoSpendReport] pivot error', err);
      setPivotError(err?.response?.data?.error || err.message || 'Canli Meta verisi alinamadi.');
      setPivot({ campaigns: [], totals: null });
    } finally {
      setPivotLoading(false);
    }
  }, [brandId, dateRange, customStart, customEnd]);

  useEffect(() => {
    fetchData();
    fetchPivot();
  }, [fetchData, fetchPivot]);

  // ---- Agregasyon: toplamlar + kampanya/creative kirilimi ----
  const { totals, campaigns } = useMemo(() => {
    const t = { spend: 0, impressions: 0, clicks: 0, purchases: 0, revenue: 0, ads: 0 };
    const campMap = {};

    rows.forEach((r) => {
      const spend = num(r.spend ?? r.cost);
      const impressions = num(r.impressions);
      const clicks = num(r.clicks);
      const purchases = num(r.purchases ?? r.conversions);
      const revenue = num(r.revenue ?? r.conversion_value);

      t.spend += spend;
      t.impressions += impressions;
      t.clicks += clicks;
      t.purchases += purchases;
      t.revenue += revenue;

      const cKey = r.campaign_id || r.campaign_name || 'unknown';
      const camp = (campMap[cKey] = campMap[cKey] || {
        id: cKey,
        name: r.campaign_name || '(isimsiz kampanya)',
        spend: 0,
        impressions: 0,
        clicks: 0,
        purchases: 0,
        revenue: 0,
        adsMap: {},
      });
      camp.spend += spend;
      camp.impressions += impressions;
      camp.clicks += clicks;
      camp.purchases += purchases;
      camp.revenue += revenue;

      const aKey = r.creative_id || r.ad_id || r.ad_name || 'ad';
      const ad = (camp.adsMap[aKey] = camp.adsMap[aKey] || {
        key: aKey,
        creative_id: r.creative_id || null,
        ad_name: r.ad_name || null,
        adset_name: r.adset_name || null,
        title: r.title || null,
        body: r.body || null,
        spend: 0,
        impressions: 0,
        clicks: 0,
        purchases: 0,
        revenue: 0,
      });
      ad.spend += spend;
      ad.impressions += impressions;
      ad.clicks += clicks;
      ad.purchases += purchases;
      ad.revenue += revenue;
    });

    const campaigns = Object.values(campMap)
      .map((c) => ({
        ...c,
        ads: Object.values(c.adsMap).sort((a, b) => b.spend - a.spend),
      }))
      .sort((a, b) => b.spend - a.spend);

    t.ads = campaigns.reduce((s, c) => s + c.ads.length, 0);
    return { totals: t, campaigns };
  }, [rows]);

  const roas = totals.spend > 0 ? totals.revenue / totals.spend : 0;

  // Ust ozet + ROAS canli Meta pivotundan beslenir; pivot gelmezse view verisine duser.
  const lt = pivot.totals;
  const disp = {
    spend: lt ? lt.spend : totals.spend,
    impressions: lt ? lt.impressions : totals.impressions,
    clicks: lt ? lt.clicks : totals.clicks,
    purchases: lt ? lt.conversions : totals.purchases,
    revenue: lt ? lt.revenue : totals.revenue,
  };
  const dispRoas = disp.spend > 0 ? disp.revenue / disp.spend : 0;
  const dispCampaignCount = pivot.campaigns.length || campaigns.length;
  const isLive = !!lt;

  const mediaFor = (ad) => {
    const m = ad.creative_id ? mediaMap[ad.creative_id] : null;
    const directUrl = m?.best_media_url || m?.image_url || m?.thumbnail_url || m?.video_thumbnail_url || '';
    const proxyUrl =
      ad.creative_id && brandId
        ? `${API}/api/meta-ads/live/creative-thumbnail?creativeId=${encodeURIComponent(
            ad.creative_id
          )}&brandId=${encodeURIComponent(brandId)}&w=400&h=400`
        : '';
    return {
      isVideo: !!m?.is_video,
      title: m?.title || ad.title || m?.name || ad.ad_name || '',
      body: m?.body || ad.body || '',
      primary: proxyUrl || directUrl || FALLBACK_IMG,
      fallback: directUrl || FALLBACK_IMG,
    };
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-indigo-600" />
            "Go" Kampanyalari - Meta Harcama Raporu
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Kampanya adinda <span className="font-semibold">"Go"</span> gecen tum Meta kampanyalari.{' '}
            {selectedBrand?.name ? <span className="font-medium">{selectedBrand.name}</span> : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
          >
            {DATE_RANGES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          {dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-2"
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-2"
              />
            </>
          )}
          <button
            onClick={() => {
              fetchData();
              fetchPivot();
            }}
            disabled={loading || pivotLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {loading || pivotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ---- USTTE: TOPLAM HARCAMA ---- */}
      <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                Su ana kadar toplam harcama
              </p>
              {isLive && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> CANLI
                </span>
              )}
            </div>
            <p className="text-4xl md:text-5xl font-bold text-indigo-900 mt-1">{fmtTRY(disp.spend)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {dispCampaignCount} kampanya • {totals.ads} reklam
              {lastUpdated ? ` • Guncelleme ${lastUpdated.toLocaleString('tr-TR')}` : ''}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryStat icon={Eye} label="Gosterim" value={fmtNum(disp.impressions)} color="blue" />
            <SummaryStat icon={MousePointerClick} label="Tiklama" value={fmtNum(disp.clicks)} color="amber" />
            <SummaryStat icon={ShoppingCart} label="Satin alma" value={fmtNum(disp.purchases)} color="emerald" />
            <SummaryStat
              icon={TrendingUp}
              label="ROAS"
              value={dispRoas > 0 ? `${dispRoas.toFixed(2)}x` : '-'}
              color="violet"
            />
          </div>
        </div>
      </div>

      {/* ---- PIVOT: KAMPANYA PERFORMANS DETAYI (canli Meta) ---- */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            Kampanya Performans Pivotu
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Anlik Meta
            </span>
          </h3>
          {pivotLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        {pivotError ? (
          <div className="flex items-center gap-2 p-4 text-sm text-rose-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {pivotError}
          </div>
        ) : pivot.campaigns.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {pivotLoading ? 'Meta’dan canli cekiliyor…' : 'Canli veride "Go" kampanyasi bulunamadi.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Kampanya</th>
                  <th className="px-4 py-2.5 text-right font-medium">Harcama</th>
                  <th className="px-4 py-2.5 text-right font-medium">Gosterim</th>
                  <th className="px-4 py-2.5 text-right font-medium">Reach</th>
                  <th className="px-4 py-2.5 text-right font-medium">Frequency</th>
                  <th className="px-4 py-2.5 text-right font-medium">Tiklama</th>
                  <th className="px-4 py-2.5 text-right font-medium">CTR</th>
                  <th className="px-4 py-2.5 text-right font-medium">CPC</th>
                  <th className="px-4 py-2.5 text-right font-medium">CPM</th>
                  <th className="px-4 py-2.5 text-right font-medium">Satin alma</th>
                  <th className="px-4 py-2.5 text-right font-medium">Gelir</th>
                  <th className="px-4 py-2.5 text-right font-medium">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pivot.campaigns.map((c) => (
                  <tr key={c.campaign_id || c.campaign_name} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800 max-w-[260px] truncate" title={c.campaign_name}>
                      {c.campaign_name}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-indigo-700">{fmtTRY(c.spend)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{fmtNum(c.impressions)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{fmtNum(c.reach)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{num(c.frequency).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{fmtNum(c.clicks)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmtPct(c.ctr)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmtTRY2(c.cpc)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmtTRY2(c.cpm)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{fmtNum(c.conversions)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{fmtTRY(c.revenue)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
                      {num(c.roas) > 0 ? `${num(c.roas).toFixed(2)}x` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {pivot.totals && (
                <tfoot>
                  <tr className="bg-gray-800 text-white font-semibold">
                    <td className="px-4 py-2.5">Toplam ({pivot.campaigns.length})</td>
                    <td className="px-4 py-2.5 text-right">{fmtTRY(pivot.totals.spend)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtNum(pivot.totals.impressions)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtNum(pivot.totals.reach)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {(pivot.totals.reach > 0 ? pivot.totals.impressions / pivot.totals.reach : 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5 text-right">{fmtNum(pivot.totals.clicks)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {fmtPct(pivot.totals.impressions > 0 ? (pivot.totals.clicks / pivot.totals.impressions) * 100 : 0)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {fmtTRY2(pivot.totals.clicks > 0 ? pivot.totals.spend / pivot.totals.clicks : 0)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {fmtTRY2(pivot.totals.impressions > 0 ? (pivot.totals.spend / pivot.totals.impressions) * 1000 : 0)}
                    </td>
                    <td className="px-4 py-2.5 text-right">{fmtNum(pivot.totals.conversions)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtTRY(pivot.totals.revenue)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {pivot.totals.spend > 0 ? `${(pivot.totals.revenue / pivot.totals.spend).toFixed(2)}x` : '-'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ---- ALTTA: KAMPANYALAR + CREATIVE PREVIEW ---- */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yukleniyor...
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          Adinda "Go" gecen Meta kampanyasi bulunamadi.
        </div>
      ) : (
        <div className="space-y-6">
          {campaigns.map((camp) => (
            <div key={camp.id} className="rounded-2xl border bg-white shadow-sm overflow-hidden">
              {/* Kampanya basligi */}
              <div className="bg-gray-50 border-b px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-800 truncate">{camp.name}</h3>
                  <p className="text-xs text-gray-500">
                    {camp.ads.length} reklam • {fmtNum(camp.impressions)} gosterim • {fmtNum(camp.clicks)} tiklama
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-500">Kampanya harcamasi</p>
                  <p className="text-xl font-bold text-indigo-700">{fmtTRY(camp.spend)}</p>
                </div>
              </div>

              {/* Creative preview grid */}
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {camp.ads.map((ad) => {
                  const media = mediaFor(ad);
                  const previewHtml = ad.creative_id ? previewMap[ad.creative_id] : null;
                  return (
                    <div key={ad.key} className="rounded-xl border border-gray-200 overflow-hidden flex flex-col">
                      {previewHtml ? (
                        // Meta canli ad preview (creative-performance ile ayni mantik)
                        <div
                          className="meta-preview-frame w-full overflow-hidden bg-white flex justify-center"
                          style={{ height: 460 }}
                          dangerouslySetInnerHTML={{
                            __html: `<style>.meta-preview-frame iframe{width:100%!important;height:680px!important;border:none!important;overflow:hidden!important;}</style>${previewHtml
                              .replace(/scrolling\s*=\s*["']yes["']/gi, 'scrolling="no"')
                              .replace(/height\s*=\s*["']\d+["']/gi, 'height="680"')}`,
                          }}
                        />
                      ) : (
                        <div className="relative aspect-square bg-gray-100">
                          <img
                            src={media.primary}
                            alt={media.title || 'Creative'}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onLoad={(e) => {
                              const img = e.currentTarget;
                              if (img.naturalWidth <= 1 && img.naturalHeight <= 1) {
                                img.src = media.fallback;
                              }
                            }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = media.fallback;
                            }}
                          />
                          {media.isVideo && (
                            <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5">
                              <Play className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-3 flex flex-col gap-1 flex-1">
                        <p className="text-xs font-semibold text-gray-800 line-clamp-2">
                          {media.title || ad.ad_name || 'Reklam'}
                        </p>
                        {media.body && <p className="text-[11px] text-gray-500 line-clamp-2">{media.body}</p>}
                        <div className="mt-auto pt-2 flex items-center justify-between border-t border-gray-100">
                          <span className="text-[10px] text-gray-400">{fmtNum(ad.impressions)} gosterim</span>
                          <span className="text-sm font-bold text-emerald-700">{fmtTRY(ad.spend)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'text-blue-700 bg-blue-50 border-blue-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    violet: 'text-violet-700 bg-violet-50 border-violet-100',
  };
  return (
    <div className={`rounded-xl border px-3 py-2 ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="text-lg font-bold mt-0.5">{value}</p>
    </div>
  );
}
